import { useEffect, useRef } from "react";
import useApi from "./useApi";
import { useCerberus } from "./useCerberus";
import SessionService from "../services/SessionService";

const HEARTBEAT_INTERVAL_MS = 60 * 1000;

/**
 * Polls `/session/status` for notification/message counts and the authoritative session expiry.
 *
 * This poll is deliberately PASSIVE: the endpoint is marked [NoSessionExtension] on the server, so
 * it reports the expiry without sliding it. That is the whole point — the app polls itself on a
 * timer whether or not anyone is at the keyboard, so if the heartbeat extended the session, an
 * unattended open tab would keep a PHI-bearing session alive indefinitely and the idle timeout
 * would be enforced only by client-side JS. Real user presence is signalled separately — see
 * InactivityHandler -> SessionService.extend().
 */
function useHeartbeat() {
  const api = useApi();
  const { updateNotificationCount, updateUnreadMessagesCount } = useCerberus();
  const sessionService = SessionService.getInstance();

  // The Cerberus context recreates these callbacks on every render, so depending on them directly
  // tore the interval down and rebuilt it constantly. Hold them in refs and keep the effect's deps
  // stable. This also replaces a fragile "skip the first mount" StrictMode hack that only worked
  // BECAUSE those deps were unstable — in production (no StrictMode double-invoke) with stable
  // deps, that early-return meant the heartbeat would never start at all.
  const notifyRef = useRef(updateNotificationCount);
  const unreadRef = useRef(updateUnreadMessagesCount);
  notifyRef.current = updateNotificationCount;
  unreadRef.current = updateUnreadMessagesCount;

  useEffect(() => {
    let cancelled = false;

    const fetchStatus = async () => {
      try {
        const { data } = await api.post<{
          notificationCount: number;
          unreadMessagesCount: number;
          expiresIn: number;
          canExtend: boolean;
          warningSeconds: number;
        }>(`/session/status`);

        if (cancelled) return;

        // Mirror the server's expiry so the countdown UI is truthful.
        sessionService.setSession(data.expiresIn, {
          canExtend: data.canExtend,
          warningSeconds: data.warningSeconds,
        });

        notifyRef.current(data.notificationCount);
        unreadRef.current(data.unreadMessagesCount);
      } catch (error) {
        if (cancelled) return;
        // The server is authoritative: if it won't tell us our status, the session is gone.
        console.error("Failed to send heartbeat", error);
        sessionService.clearSession();
        window.location.href = "/login";
      }
    };

    fetchStatus();
    const id = setInterval(fetchStatus, HEARTBEAT_INTERVAL_MS);

    // Idempotent under StrictMode's double-invoke: cleanup cancels the in-flight call and clears
    // the interval, so a remount simply starts a fresh one.
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [api, sessionService]);
}

export default useHeartbeat;
